import type { MapCenter } from "@/types/map";
import { useMap } from "@vis.gl/react-google-maps";
import { useEffect } from "react";

/** Continental US fallback when no pins are available. */
export const INSPECTION_MAP_DEFAULT_CENTER: MapCenter = {
  lat: 39.8283,
  lng: -98.5795,
};

/** Initial zoom before fitBounds runs. */
export const INSPECTION_MAP_DEFAULT_ZOOM = 12;

/**
 * Cap automatic fitBounds (and user zoom) so a single pin or tight cluster
 * does not zoom to building/street-extreme level. Aligns with marker-cluster
 * maxZoom used elsewhere in the app.
 */
export const INSPECTION_MAP_MAX_ZOOM = 15;

/** Pixel padding around pins when fitting bounds. */
export const INSPECTION_MAP_FIT_PADDING = 48;

type LatLngPin = {
  latitude: number;
  longitude: number;
};

/**
 * Fit the map to pins (and an optional extra point such as the user's
 * location), then clamp zoom so single-marker / tight-cluster views stay at
 * neighborhood scale.
 */
export function useInspectionMapFitBounds(
  pins: LatLngPin[],
  extraPoint?: MapCenter | null,
) {
  const map = useMap();
  const extraLat = extraPoint?.lat;
  const extraLng = extraPoint?.lng;

  useEffect(() => {
    if (!map || pins.length === 0) return;

    const bounds = new google.maps.LatLngBounds();
    for (const pin of pins) {
      bounds.extend({ lat: pin.latitude, lng: pin.longitude });
    }
    if (extraLat != null && extraLng != null) {
      bounds.extend({ lat: extraLat, lng: extraLng });
    }

    map.fitBounds(bounds, INSPECTION_MAP_FIT_PADDING);

    // fitBounds is async; clamp after the map settles so maxZoom is honored
    // even when Google temporarily overshoots for a degenerate single-point bound.
    const listener = map.addListener("idle", () => {
      google.maps.event.removeListener(listener);
      const zoom = map.getZoom();
      if (zoom != null && zoom > INSPECTION_MAP_MAX_ZOOM) {
        map.setZoom(INSPECTION_MAP_MAX_ZOOM);
      }
    });

    return () => {
      google.maps.event.removeListener(listener);
    };
  }, [map, pins, extraLat, extraLng]);
}
